'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import {
  BadgeCheck,
  ChevronDown,
  Search,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Segmented } from '@/components/ui/segmented';
import { TimezonePicker } from '@/components/layout/timezone-picker';
import { MORE_NAV, PRIMARY_NAV, isActive, visible } from '@/components/layout/nav-config';
import { MarketStrip } from '@/components/workspace/market-strip';
import { FlattenAllButton } from '@/components/platform/flatten-all-button';
import { GlobalLiveSwitch } from '@/components/platform/global-live-switch';
import { HardFreezeToggle } from '@/components/platform/hard-freeze-toggle';
import { useWsConnectionState, useWsDiagnostics } from '@/lib/ws/manager';
import { useSessionStore } from '@/lib/auth/session';
import { ROLE_LABEL } from '@/lib/auth/roles';
import { LAYOUT_HOME, useLayoutModeStore, type LayoutMode } from '@/lib/ui/layout-mode';
import { cn } from '@/lib/util/cn';

const THEMES = [
  { id: 'dark', label: 'Dark' },
  { id: 'light', label: 'Light' },
  { id: 'hc', label: 'High contrast' },
  { id: 'amber', label: 'Amber floor' },
] as const;

const DENSITIES = [
  { id: 'comfortable', label: 'Comfortable' },
  { id: 'compact', label: 'Compact' },
  { id: 'bloomberg', label: 'Bloomberg' },
] as const;

type ThemeId = (typeof THEMES)[number]['id'];
type DensityId = (typeof DENSITIES)[number]['id'];

const STATE_TONE = {
  idle: { dot: 'bg-fg-muted', label: 'text-fg-muted' },
  connecting: { dot: 'bg-warn', label: 'text-warn' },
  // "connected" is a STATUS (green), not a direction — must not use --long (blue).
  open: { dot: 'bg-success', label: 'text-success' },
  reconnecting: { dot: 'bg-warn', label: 'text-warn' },
  closed: { dot: 'bg-fg-muted', label: 'text-fg-muted' },
  error: { dot: 'bg-crit', label: 'text-crit' },
} as const;

export function Topbar({ mode }: { mode: LayoutMode }) {
  const me = useSessionStore((s) => s.me);
  const signOut = useSessionStore((s) => s.signOut);
  const router = useRouter();
  const pathname = usePathname();
  const role = me?.role ?? null;
  const moreGroups = MORE_NAV.map((g) => ({ ...g, items: g.items.filter((i) => visible(i, role)) })).filter(
    (g) => g.items.length > 0,
  );
  const moreActive = moreGroups.some((g) => g.items.some((i) => isActive(pathname, i.href)));

  const wsState = useWsConnectionState();
  const wsDiag = useWsDiagnostics();
  const [hydrated, setHydrated] = useState(false);
  const [theme, setTheme] = useState<ThemeId>('dark');
  const [density, setDensity] = useState<DensityId>('comfortable');

  useEffect(() => {
    setHydrated(true);
    if (typeof document !== 'undefined') {
      setTheme((document.documentElement.getAttribute('data-theme') as ThemeId) ?? 'dark');
      setDensity(
        (document.documentElement.getAttribute('data-density') as DensityId) ?? 'comfortable',
      );
    }
  }, []);

  const applyTheme = (next: ThemeId) => {
    setTheme(next);
    document.documentElement.setAttribute('data-theme', next);
    try {
      localStorage.setItem('te:theme', next);
    } catch {
      /* ignored */
    }
  };
  const applyDensity = (next: DensityId) => {
    setDensity(next);
    document.documentElement.setAttribute('data-density', next);
    try {
      localStorage.setItem('te:density', next);
    } catch {
      /* ignored */
    }
  };

  const openPalette = () => {
    // The palette listens for ⌘K globally. We dispatch the same combo.
    window.dispatchEvent(
      new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }),
    );
  };

  const terminal = mode === 'terminal';

  return (
    <div className="shrink-0">
    <header
      data-app-shell="topbar"
      className={cn(
        'flex h-12 items-center border-b border-border bg-bg-elev px-4',
        terminal ? 'gap-6' : 'gap-4',
      )}
    >
      {terminal ? (
        <>
          <Link href={LAYOUT_HOME.terminal as never} className="shrink-0 whitespace-nowrap font-display text-[15px] font-semibold tracking-tight text-fg">
            Trading Engine
          </Link>
          <nav className="flex min-w-0 items-center gap-1 overflow-x-auto" aria-label="Primary navigation">
            {PRIMARY_NAV.filter((i) => visible(i, role)).map((i) => (
              <Link
                key={i.href}
                href={i.href as never}
                className={cn(
                  'shrink-0 rounded px-2.5 py-1.5 text-[13px] transition-colors',
                  isActive(pathname, i.href) ? 'font-medium text-fg' : 'text-fg-muted hover:text-fg',
                )}
              >
                {i.label}
              </Link>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    'inline-flex shrink-0 items-center gap-1 rounded px-2.5 py-1.5 text-[13px] transition-colors',
                    moreActive ? 'font-medium text-fg' : 'text-fg-muted hover:text-fg',
                  )}
                >
                  More <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {moreGroups.map((g, gi) => (
                  <div key={g.label ?? `g${gi}`}>
                    {gi > 0 ? <DropdownMenuSeparator /> : null}
                    {g.label ? <DropdownMenuLabel>{g.label}</DropdownMenuLabel> : null}
                    {g.items.map((i) => (
                      <DropdownMenuItem
                        key={i.href}
                        onSelect={() => router.push(i.href as never)}
                        className={cn(isActive(pathname, i.href) && 'font-medium text-fg')}
                      >
                        {i.label}
                      </DropdownMenuItem>
                    ))}
                  </div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={openPalette}
            className="inline-flex h-8 w-80 max-w-[40vw] items-center gap-2 rounded-md border border-border bg-surface px-2.5 text-[13px] text-fg-subtle hover:border-border-strong"
            aria-label="Search (⌘K)"
          >
            <Search className="h-4 w-4 shrink-0 stroke-[1.75]" aria-hidden />
            <span className="flex-1 truncate text-left">Search strategies, accounts, instruments</span>
            <kbd className="rounded border border-border px-1 text-2xs">⌘K</kbd>
          </button>
          <TimezonePicker />
        </>
      )}

      <div className="ml-auto flex shrink-0 items-center gap-2">
        {terminal ? (
          <button
            type="button"
            onClick={openPalette}
            className="grid h-8 w-8 place-items-center rounded text-fg-subtle hover:bg-surface-inset hover:text-fg"
            aria-label="Search (⌘K)"
            title="Search  ⌘K"
          >
            <Search className="h-4 w-4 stroke-[1.75]" aria-hidden />
          </button>
        ) : null}
        <LayoutToggle />
        <FlattenAllButton />
        <HardFreezeToggle />
        <GlobalLiveSwitch />
        <ConnectionPill state={wsState} pingMs={wsDiag.lastPingRttMs} />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-9 gap-2.5 rounded-md px-1.5 pr-2.5 hover:bg-surface"
            >
              <span className="grid h-7 w-7 place-items-center rounded-full border border-border bg-surface-elev text-2xs font-semibold text-fg-muted">
                {initials(me?.display_name)}
              </span>
              <span className="text-sm text-fg">{me?.display_name ?? 'Operator'}</span>
              <ChevronDown className="h-3 w-3 stroke-[1.5]" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel>
              {me === null ? 'Operator' : ROLE_LABEL[me.role]}
            </DropdownMenuLabel>
            <DropdownMenuItem disabled>{me?.email ?? 'No session'}</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Theme</DropdownMenuLabel>
            {THEMES.map((t) => (
              <DropdownMenuItem
                key={t.id}
                onSelect={() => applyTheme(t.id)}
                className={cn(hydrated && theme === t.id && 'text-fg font-medium')}
              >
                {t.label}
                {hydrated && theme === t.id ? <BadgeCheck className="ml-auto h-3.5 w-3.5 stroke-[1.5]" aria-hidden /> : null}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Density</DropdownMenuLabel>
            {DENSITIES.map((d) => (
              <DropdownMenuItem
                key={d.id}
                onSelect={() => applyDensity(d.id)}
                className={cn(hydrated && density === d.id && 'text-fg font-medium')}
              >
                {d.label}
                {hydrated && density === d.id ? <BadgeCheck className="ml-auto h-3.5 w-3.5 stroke-[1.5]" aria-hidden /> : null}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                signOut('user');
                router.replace('/sign-in');
              }}
            >
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
    {terminal ? (
      <div className="flex h-9 items-center gap-4 border-b border-border bg-bg-elev px-4">
        <MarketStrip className="flex-1" />
        <TimezonePicker />
      </div>
    ) : null}
    </div>
  );
}

/** Simple ⇄ Terminal shell switch. Persisted per browser; pages are identical in both. */
function LayoutToggle() {
  const mode = useLayoutModeStore((s) => s.mode);
  const setMode = useLayoutModeStore((s) => s.setMode);
  return (
    <Segmented<LayoutMode>
      size="sm"
      ariaLabel="Layout"
      value={mode}
      onChange={setMode}
      className="h-8"
      options={[
        { value: 'simple', label: 'Simple' },
        { value: 'terminal', label: 'Terminal' },
      ]}
    />
  );
}

function initials(name: string | null | undefined): string {
  const parts = (name ?? 'Operator').replace(/\(.*?\)/g, '').trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? 'O') + (parts[1]?.[0] ?? '')).toUpperCase();
}

function ConnectionPill({
  state,
  pingMs,
}: {
  state: keyof typeof STATE_TONE;
  pingMs: number | null;
}) {
  const tone = STATE_TONE[state];
  return (
    <span
      className="grid h-8 w-8 place-items-center"
      title={`Live feed: ${state}${pingMs !== null ? ` · ${pingMs} ms` : ''}`}
      aria-label={`WebSocket state ${state}`}
    >
      <span className={cn('h-2 w-2 rounded-full', tone.dot)} />
    </span>
  );
}
